import { PageContainer } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Card, Empty, Skeleton } from 'antd'
import { Clock3, NotebookTabs, Users } from 'lucide-react'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { ChartPanel } from './chart-panel.tsx'
import { DASHBOARD_ICONS } from './const/dashboard.ts'
import { MetricCard } from './metric-card.tsx'
import { trendOption } from './utils/chart-options.ts'
import { snapshotTime } from './utils/format.ts'
import { snapshotQueryOptions } from './utils/snapshot-query.ts'

export function DashboardPage() {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.analytics.dashboard.queryOptions(),
    ...snapshotQueryOptions
  })
  const data = query.data
  return (
    <PageContainer
      title="业务看板"
      breadcrumbRender={false}
      content="人员、客户与业务规模概览，掌握每日业务变化。"
      extra={
        <span className="bg-accent/10 text-accent inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium">
          <Clock3 className="size-3.5" aria-hidden />
          每小时更新
        </span>
      }
    >
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          title="看板加载失败"
          description={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      ) : !data ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="text-content-muted flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-content inline-flex items-center gap-2 text-sm font-medium">
              <span className="bg-accent size-1.5 rounded-full" aria-hidden />
              {data.month} · 本月动态
            </span>
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <Clock3 className="size-3.5" aria-hidden />
              更新于 {snapshotTime(data.generatedAt)} · 下次更新 {snapshotTime(data.expiresAt)}
            </span>
          </div>
          {!data.counts.length && <Empty description="当前账号暂无可查看的业务统计，请联系管理员分配业务查看权限。" />}
          {(data.orders || data.customers) && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {data.orders && (
                <MetricCard
                  title="本月新增订单"
                  value={data.orders.current}
                  previous={data.orders.previous}
                  year={data.orders.year}
                  icon={NotebookTabs}
                  emphasis="accent"
                />
              )}
              {data.customers && (
                <MetricCard
                  title="本月新增客户"
                  value={data.customers.current}
                  previous={data.customers.previous}
                  year={data.customers.year}
                  icon={Users}
                />
              )}
            </div>
          )}
          <div
            className={cn('grid min-w-0 grid-cols-1 gap-5', data.orders && data.counts.length > 0 && 'xl:grid-cols-3')}
          >
            {data.orders && (
              <div className={cn('min-w-0', data.counts.length > 0 && 'xl:col-span-2')}>
                <ChartPanel
                  title="订单趋势"
                  extra={
                    <span className="bg-surface-muted text-content-muted rounded-full px-3 py-1 text-xs">近 7 天</span>
                  }
                  description="按北京时间统计，包含今天；租赁订单和服务订单分开展示。"
                  empty={!data.daily.some((d) => d.rental + d.service > 0)}
                  option={trendOption(
                    data.daily.map((d) => d.label),
                    [
                      { name: '租赁订单', data: data.daily.map((d) => d.rental), type: 'bar' },
                      { name: '服务订单', data: data.daily.map((d) => d.service), type: 'bar' }
                    ]
                  )}
                >
                  <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-content text-3xl font-semibold tracking-tight tabular-nums">
                      {data.daily.reduce((total, d) => total + d.rental + d.service, 0).toLocaleString('zh-CN')}
                    </span>
                    <span className="text-content-muted text-xs">笔订单 · 七天合计</span>
                  </div>
                </ChartPanel>
              </div>
            )}
            {data.counts.length > 0 && (
              <Card
                title={<h2 className="m-0 text-base font-semibold">业务规模</h2>}
                extra={<span className="text-content-muted text-xs">当前数量</span>}
                className="min-w-0 rounded-2xl!"
                classNames={{ header: 'px-5 sm:px-6', body: 'p-5 sm:p-6' }}
              >
                <dl className="m-0 grid grid-cols-2 gap-x-5 gap-y-5 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                  {data.counts.map((item) => {
                    const Icon = DASHBOARD_ICONS[item.key]
                    return (
                      <div key={item.key} className="border-border/80 min-w-0 border-b pb-4">
                        <dt className="text-content-muted mb-2 flex flex-wrap items-center gap-1.5 text-xs">
                          {Icon && <Icon className="text-content-soft size-3.5 shrink-0" aria-hidden />}
                          {item.label}
                        </dt>
                        <dd className="text-content m-0 text-2xl font-semibold tracking-tight tabular-nums">
                          {item.value.toLocaleString('zh-CN')}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              </Card>
            )}
          </div>
          <p className="text-content-muted m-0 text-xs">
            订单排除已取消记录；客户排除已删除记录。人员、主体、场地和业务统计启用数量。环比对比上月同期，同比对比去年同月同期；增长率
            =（本期 − 对比期）÷ 对比期 × 100%。对比值为零时无法计算百分比；两期都为零显示持平。
          </p>
        </div>
      )}
    </PageContainer>
  )
}
